import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import "./index.css";

type ParityMode = "strict" | "corrected";

type StrengthDefinition = {
	name: string;
	labelMg: number;
	overageMg: number;
	expFactor: number;
};

type PackagingMaterial = {
	id: string;
	name: string;
	costPerUnit: number;
};

type Supplier = {
	id: string;
	name: string;
	apiPricePerKg: number;
	handlingPct: number;
	otherPct: number;
};

type PackType = string; // Now dynamic based on packaging materials

type SkuRow = {
	id: string;
	strength: string;
	packType: PackType;
	packSize: number;
	supplierId: string; // Reference to supplier ID
};

type Batch = {
	size: 1000000 | 250000;
	ccpcPerBatch: number;
};

type SortConfig = {
	column:
		| "strength"
		| "packType"
		| "supplier"
		| "size"
		| "api"
		| "excipient"
		| "pm"
		| "ccpc"
		| "serialization"
		| "total";
	direction: "asc" | "desc";
};

type State = {
	suppliers: Supplier[];
	currency: string;
	strengths: StrengthDefinition[];
	batch: Batch;
	serializationPerUnit: number;
	packagingMaterials: PackagingMaterial[];
	parityMode: ParityMode;
	sortConfig: SortConfig;
	skus: SkuRow[];
};

function formatCurrency(amount: number, currency: string) {
	try {
		return new Intl.NumberFormat(undefined, {
			style: "currency",
			currency,
			maximumFractionDigits: 4,
		}).format(Number.isFinite(amount) ? amount : 0);
	} catch {
		return `${currency} ${amount.toFixed(4)}`;
	}
}

function toNonNegativeNumber(value: string | number) {
	const n =
		typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
	return Number.isFinite(n) && n >= 0 ? n : 0;
}

function SortableHeader({
	children,
	column,
	sortConfig,
	onSort,
	className,
}: {
	children: React.ReactNode;
	column: SortConfig["column"];
	sortConfig: SortConfig;
	onSort: (column: SortConfig["column"]) => void;
	className?: string;
}) {
	const isActive = sortConfig.column === column;
	const direction = isActive ? sortConfig.direction : null;

	return (
		<button
			type="button"
			className={`flex w-full items-center gap-1 hover:bg-muted/50 rounded px-1 py-1 text-xs font-medium text-muted-foreground cursor-pointer ${className || ""}`}
			onClick={() => onSort(column)}
		>
			{children}
			{isActive && (
				<span className="text-xs">{direction === "asc" ? "↑" : "↓"}</span>
			)}
		</button>
	);
}

function clampPct(value: number) {
	if (!Number.isFinite(value)) return 0;
	if (value < 0) return 0;
	if (value > 100) return 100;
	return value;
}

export function App() {
	const [state, setState] = useState<State>({
		suppliers: [
			{
				id: crypto.randomUUID(),
				name: "Aurobindo",
				apiPricePerKg: 750,
				handlingPct: 5,
				otherPct: 0,
			},
			{
				id: crypto.randomUUID(),
				name: "Lupin",
				apiPricePerKg: 500,
				handlingPct: 5,
				otherPct: 0,
			},
		],
		currency: "USD",
		strengths: [
			{ name: "10 mg", labelMg: 10, overageMg: 0.526, expFactor: 0.00071 },
			{ name: "25 mg", labelMg: 25, overageMg: 0.316, expFactor: 0.00176 },
		],
		batch: { size: 1000000, ccpcPerBatch: 23000 },
		serializationPerUnit: 0.06,
		packagingMaterials: [
			{ id: crypto.randomUUID(), name: "Bottle", costPerUnit: 0.00847 },
			{ id: crypto.randomUUID(), name: "Blister", costPerUnit: 0.00482 },
		],
		parityMode: "strict",
		sortConfig: { column: "total", direction: "asc" },
		skus: [
			{
				id: crypto.randomUUID(),
				strength: "10 mg",
				packType: "Bottle",
				packSize: 30,
				supplierId: "", // Will be set to first supplier's ID
			},
		],
	});

	// Initialize supplier ID for default SKU
	useEffect(() => {
		if (
			state.skus.length > 0 &&
			state.skus[0] &&
			state.skus[0].supplierId === "" &&
			state.suppliers.length > 0 &&
			state.suppliers[0]
		) {
			setState((s) => ({
				...s,
				skus: s.skus.map((sku, idx) =>
					idx === 0 && s.suppliers[0]
						? { ...sku, supplierId: s.suppliers[0].id }
						: sku,
				),
			}));
		}
	}, [state.suppliers, state.skus]);

	const totalApiPricePerKgBySupplier = useMemo(() => {
		return state.suppliers.reduce(
			(acc, supplier) => {
				const multiplier =
					1 +
					clampPct(supplier.handlingPct) / 100 +
					clampPct(supplier.otherPct) / 100;
				acc[supplier.id] = supplier.apiPricePerKg * multiplier;
				return acc;
			},
			{} as Record<string, number>,
		);
	}, [state.suppliers]);

	const ccpcPerUnit = useMemo(() => {
		if (!state.batch.size) return 0;
		return state.batch.ccpcPerBatch / state.batch.size;
	}, [state.batch]);

	const perStrengthBySupplier = useMemo(() => {
		return state.suppliers.reduce(
			(acc, supplier) => {
				const pricePerKg = totalApiPricePerKgBySupplier[supplier.id] || 0;
				acc[supplier.id] = state.strengths.map((s) => {
					const apiPerUnitMg = s.labelMg + s.overageMg;
					const apiPricePerUnit = (apiPerUnitMg * pricePerKg) / 1_000_000;
					const expPerUnit = apiPricePerUnit * s.expFactor;
					return {
						name: s.name,
						apiPerUnitMg,
						apiPricePerUnit,
						expPerUnit,
						expFactor: s.expFactor,
					};
				});
				return acc;
			},
			{} as Record<
				string,
				Array<{
					name: string;
					apiPerUnitMg: number;
					apiPricePerUnit: number;
					expPerUnit: number;
					expFactor: number;
				}>
			>,
		);
	}, [state.strengths, totalApiPricePerKgBySupplier, state.suppliers]);

	const findStrengthBySupplier = useCallback(
		(supplierId: string, name: string) => {
			return perStrengthBySupplier[supplierId]?.find((s) => s.name === name);
		},
		[perStrengthBySupplier],
	);

	const pmPerUnitForPackType = useCallback(
		(packType: PackType): number => {
			const material = state.packagingMaterials.find(
				(m) => m.name === packType,
			);
			return material?.costPerUnit ?? 0;
		},
		[state.packagingMaterials],
	);

	const computeSkuTotals = useCallback(
		(row: SkuRow) => {
			const strength = findStrengthBySupplier(row.supplierId, row.strength);
			if (!strength) {
				return {
					valid: false,
					reason: "Missing strength",
					apiPerPack: 0,
					excipientPerPack: 0,
					pmPerPack: 0,
					ccpcPerPack: 0,
					serializationPerPack: 0,
					totalPackCost: 0,
					percentApi: 0,
					percentOther: 0,
					percentCcpc: 0,
				};
			}
			const units = Math.max(1, Math.floor(row.packSize || 0));
			const apiPerPack = units * strength.apiPricePerUnit;
			const excipientPerPack = apiPerPack * strength.expFactor;
			const pmPerPack = units * pmPerUnitForPackType(row.packType);
			const ccpcPerPack = units * ccpcPerUnit;
			const serializationPerPack = units * state.serializationPerUnit;
			const totalPackCost =
				apiPerPack +
				excipientPerPack +
				pmPerPack +
				ccpcPerPack +
				serializationPerPack;
			const denom = totalPackCost > 0 ? totalPackCost : 1;
			const percentApi = apiPerPack / denom;
			const percentCcpc = ccpcPerPack / denom;
			const percentOther =
				(excipientPerPack + pmPerPack + serializationPerPack) / denom;
			return {
				valid: true,
				reason: "",
				apiPerPack,
				excipientPerPack,
				pmPerPack,
				ccpcPerPack,
				serializationPerPack,
				totalPackCost,
				percentApi,
				percentOther,
				percentCcpc,
			};
		},
		[
			findStrengthBySupplier,
			pmPerUnitForPackType,
			ccpcPerUnit,
			state.serializationPerUnit,
		],
	);

	// Create pairs of SKUs with their suppliers
	const skuPairs = useMemo(() => {
		return state.skus.map((row) => {
			const supplier = state.suppliers.find((s) => s.id === row.supplierId);
			// Ensure we always have a supplier by using the first one as fallback
			const fallbackSupplier =
				state.suppliers.length > 0
					? state.suppliers[0]
					: {
							id: "",
							name: "Unknown Supplier",
							apiPricePerKg: 0,
							handlingPct: 0,
							otherPct: 0,
						};
			return { row, supplier: supplier || fallbackSupplier };
		});
	}, [state.skus, state.suppliers]);

	// Sort SKUs only when user manually clicks column headers
	const sortedSkus = useMemo(() => {
		// If no sorting is selected, preserve original order
		if (
			state.sortConfig.column === "total" &&
			state.sortConfig.direction === "asc"
		) {
			return skuPairs;
		}

		// Create a stable sort that preserves original order for equal items
		const stableSort = (
			array: typeof skuPairs,
			compareFn: (a: (typeof skuPairs)[0], b: (typeof skuPairs)[0]) => number,
		) => {
			return array
				.map((item, index) => ({ item, index }))
				.sort((a, b) => {
					const result = compareFn(a.item, b.item);
					return result !== 0 ? result : a.index - b.index;
				})
				.map(({ item }) => item);
		};

		const multiplier = state.sortConfig.direction === "asc" ? 1 : -1;

		switch (state.sortConfig.column) {
			case "strength":
				return stableSort(
					skuPairs,
					(a, b) => multiplier * a.row.strength.localeCompare(b.row.strength),
				);
			case "packType":
				return stableSort(
					skuPairs,
					(a, b) => multiplier * a.row.packType.localeCompare(b.row.packType),
				);
			case "supplier":
				return stableSort(
					skuPairs,
					(a, b) =>
						multiplier *
						(a.supplier?.name ?? "").localeCompare(b.supplier?.name ?? ""),
				);
			case "size":
				return stableSort(
					skuPairs,
					(a, b) => multiplier * (a.row.packSize - b.row.packSize),
				);
			case "api":
				return stableSort(
					skuPairs,
					(a, b) =>
						multiplier *
						(computeSkuTotals(a.row).apiPerPack -
							computeSkuTotals(b.row).apiPerPack),
				);
			case "excipient":
				return stableSort(
					skuPairs,
					(a, b) =>
						multiplier *
						(computeSkuTotals(a.row).excipientPerPack -
							computeSkuTotals(b.row).excipientPerPack),
				);
			case "pm":
				return stableSort(
					skuPairs,
					(a, b) =>
						multiplier *
						(computeSkuTotals(a.row).pmPerPack -
							computeSkuTotals(b.row).pmPerPack),
				);
			case "ccpc":
				return stableSort(
					skuPairs,
					(a, b) =>
						multiplier *
						(computeSkuTotals(a.row).ccpcPerPack -
							computeSkuTotals(b.row).ccpcPerPack),
				);
			case "serialization":
				return stableSort(
					skuPairs,
					(a, b) =>
						multiplier *
						(computeSkuTotals(a.row).serializationPerPack -
							computeSkuTotals(b.row).serializationPerPack),
				);
			case "total":
			default:
				return stableSort(
					skuPairs,
					(a, b) =>
						multiplier *
						(computeSkuTotals(a.row).totalPackCost -
							computeSkuTotals(b.row).totalPackCost),
				);
		}
	}, [skuPairs, state.sortConfig, computeSkuTotals]);
	/* eslint-enable react-hooks/exhaustive-deps */

	function handleColumnSort(column: SortConfig["column"]) {
		setState((s) => ({
			...s,
			sortConfig: {
				column,
				direction:
					s.sortConfig.column === column && s.sortConfig.direction === "asc"
						? "desc"
						: "asc",
			},
		}));
	}

	function addSku() {
		const defaultStrength = state.strengths[0]?.name ?? "";
		const defaultPackType = state.packagingMaterials[0]?.name ?? "";
		const defaultSupplierId = state.suppliers[0]?.id ?? "";
		const newRow: SkuRow = {
			id: crypto.randomUUID(),
			strength: defaultStrength,
			packType: defaultPackType,
			packSize: 30,
			supplierId: defaultSupplierId,
		};
		setState((s) => ({
			...s,
			skus: [...s.skus, newRow],
		}));
	}

	function duplicateSku(id: string) {
		setState((s) => {
			const idx = s.skus.findIndex((r) => r.id === id);
			if (idx === -1) return s;
			const source = s.skus[idx];
			if (!source) return s;
			const copy: SkuRow = {
				id: crypto.randomUUID(),
				strength: source.strength,
				packType: source.packType,
				packSize: source.packSize,
				supplierId: source.supplierId,
			};
			const skus: SkuRow[] = [
				...s.skus.slice(0, idx + 1),
				copy,
				...s.skus.slice(idx + 1),
			];
			return { ...s, skus };
		});
	}

	function deleteSku(id: string) {
		setState((s) => ({ ...s, skus: s.skus.filter((r) => r.id !== id) }));
	}

	function updateSku<K extends keyof SkuRow>(
		id: string,
		key: K,
		value: SkuRow[K],
	) {
		setState((s) => ({
			...s,
			skus: s.skus.map((r) => {
				if (r.id !== id) return r;
				const updated: SkuRow = { ...r, [key]: value } as SkuRow;
				return updated;
			}),
		}));
	}

	function addPackagingMaterial() {
		const newMaterial: PackagingMaterial = {
			id: crypto.randomUUID(),
			name: `Package ${state.packagingMaterials.length + 1}`,
			costPerUnit: 0,
		};
		setState((s) => ({
			...s,
			packagingMaterials: [...s.packagingMaterials, newMaterial],
		}));
	}

	function updatePackagingMaterial<K extends keyof PackagingMaterial>(
		id: string,
		key: K,
		value: PackagingMaterial[K],
	) {
		setState((s) => ({
			...s,
			packagingMaterials: s.packagingMaterials.map((m) => {
				if (m.id !== id) return m;
				const updated: PackagingMaterial = {
					...m,
					[key]: value,
				} as PackagingMaterial;
				return updated;
			}),
		}));
	}

	function deletePackagingMaterial(id: string) {
		// Don't allow deletion if only one packaging material exists
		if (state.packagingMaterials.length <= 1) return;

		const materialToDelete = state.packagingMaterials.find((m) => m.id === id);
		if (!materialToDelete) return;

		setState((s) => ({
			...s,
			packagingMaterials: s.packagingMaterials.filter((m) => m.id !== id),
			// Update any SKUs that use this packaging material to use the first available one
			skus: s.skus.map((sku) => {
				if (sku.packType === materialToDelete.name) {
					const firstAvailable = s.packagingMaterials.find((m) => m.id !== id);
					return { ...sku, packType: firstAvailable?.name ?? "" };
				}
				return sku;
			}),
		}));
	}

	function addSupplier() {
		const newSupplier: Supplier = {
			id: crypto.randomUUID(),
			name: `Supplier ${state.suppliers.length + 1}`,
			apiPricePerKg: 0,
			handlingPct: 0,
			otherPct: 0,
		};
		setState((s) => ({
			...s,
			suppliers: [...s.suppliers, newSupplier],
		}));
	}

	function updateSupplier<K extends keyof Supplier>(
		id: string,
		key: K,
		value: Supplier[K],
	) {
		setState((s) => ({
			...s,
			suppliers: s.suppliers.map((supplier) => {
				if (supplier.id !== id) return supplier;
				const updated: Supplier = { ...supplier, [key]: value } as Supplier;
				return updated;
			}),
		}));
	}

	function deleteSupplier(id: string) {
		// Don't allow deletion if only one supplier exists
		if (state.suppliers.length <= 1) return;

		const supplierToDelete = state.suppliers.find((s) => s.id === id);
		if (!supplierToDelete) return;

		setState((s) => ({
			...s,
			suppliers: s.suppliers.filter((supplier) => supplier.id !== id),
			// Update any SKUs that use this supplier to use the first available one
			skus: s.skus.map((sku) => {
				if (sku.supplierId === supplierToDelete.id) {
					const firstAvailable = s.suppliers.find((s) => s.id !== id);
					return { ...sku, supplierId: firstAvailable?.id ?? "" };
				}
				return sku;
			}),
		}));
	}

	function exportCsv() {
		const rows: Array<Record<string, string | number>> = sortedSkus.map(
			({ row: r, supplier }) => {
				const t = computeSkuTotals(r);
				return {
					Supplier: supplier?.name ?? "Unknown Supplier",
					Strength: r.strength,
					"Pack type": r.packType,
					"Pack size": r.packSize,
					"API per pack": t.apiPerPack,
					"Excipient per pack": t.excipientPerPack,
					"PM per pack": t.pmPerPack,
					"CCPC per pack": t.ccpcPerPack,
					"Serialization per pack": t.serializationPerPack,
					"Total pack cost": t.totalPackCost,
					"%API": t.percentApi,
					"%(Excipient + PM + Serialization)": t.percentOther,
					"%CCPC": t.percentCcpc,
				};
			},
		);
		const headers = Object.keys(rows[0] ?? {});
		const csv = [
			headers.join(","),
			...rows.map((row) =>
				headers
					.map((h) => {
						const v = row[h];
						return typeof v === "number" ? v.toFixed(6) : String(v);
					})
					.join(","),
			),
		].join("\n");
		const blob = new Blob([csv], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "sku_results.csv";
		a.click();
		URL.revokeObjectURL(url);
	}

	const cheapestAndExpensive = useMemo(() => {
		if (sortedSkus.length === 0) return null;
		const enriched = sortedSkus.map(({ row, supplier }) => ({
			sku: { row, supplier },
			total: computeSkuTotals(row).totalPackCost,
		}));
		enriched.sort((a, b) => a.total - b.total);
		const cheapest = enriched[0] ?? null;
		const mostExpensive = enriched[enriched.length - 1] ?? null;
		return { cheapest, mostExpensive };
	}, [sortedSkus, computeSkuTotals]);

	return (
		<div className="container mx-auto p-6 space-y-6">
			<div className="space-y-1">
				<h1 className="text-2xl font-bold">COGS Calculator</h1>
				<div className="text-sm text-muted-foreground">
					Batch size:{" "}
					<span className="font-medium">
						{state.batch.size.toLocaleString()}
					</span>
					<span className="ml-4">CCPC/unit:</span>
					<span className="ml-2 font-medium">
						{formatCurrency(ccpcPerUnit, state.currency)}
					</span>
				</div>
			</div>

			{/* Setup blocks in 2 columns */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<div className="space-y-4">
					<Card>
						<CardHeader>
							<CardTitle>Suppliers</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							{state.suppliers.map((supplier) => (
								<div
									key={supplier.id}
									className="grid grid-cols-12 gap-2 items-end"
								>
									<div className="col-span-2 space-y-1">
										<Label>Name</Label>
										<Input
											value={supplier.name}
											onChange={(e) =>
												updateSupplier(supplier.id, "name", e.target.value)
											}
										/>
									</div>
									<div className="col-span-2 space-y-1">
										<Label>API price/kg</Label>
										<Input
											type="number"
											min={0}
											step="0.0001"
											value={supplier.apiPricePerKg}
											onChange={(e) =>
												updateSupplier(
													supplier.id,
													"apiPricePerKg",
													toNonNegativeNumber(e.target.value),
												)
											}
										/>
									</div>

									<div className="col-span-2 space-y-1">
										<Label>Handling %</Label>
										<Input
											type="number"
											min={0}
											max={100}
											step="0.01"
											value={supplier.handlingPct}
											onChange={(e) =>
												updateSupplier(
													supplier.id,
													"handlingPct",
													clampPct(toNonNegativeNumber(e.target.value)),
												)
											}
										/>
									</div>
									<div className="col-span-2 space-y-1">
										<Label>Other charges %</Label>
										<Input
											type="number"
											min={0}
											max={100}
											step="0.01"
											value={supplier.otherPct}
											onChange={(e) =>
												updateSupplier(
													supplier.id,
													"otherPct",
													clampPct(toNonNegativeNumber(e.target.value)),
												)
											}
										/>
									</div>
									<div className="col-span-2 flex items-end gap-2">
										<Button
											variant="secondary"
											onClick={() => deleteSupplier(supplier.id)}
											disabled={state.suppliers.length <= 1}
										>
											Delete
										</Button>
									</div>
								</div>
							))}
							<div className="flex justify-end">
								<Button onClick={addSupplier}>Add supplier</Button>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>Global Settings</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label>Currency</Label>
									<Input
										type="text"
										maxLength={6}
										value={state.currency}
										onChange={(e) =>
											setState((s) => ({
												...s,
												currency: e.target.value.trim().toUpperCase() || "USD",
											}))
										}
									/>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>Batch economics & Parity</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label>Batch size</Label>
									<Select
										value={String(state.batch.size)}
										onValueChange={(v: string) =>
											setState((s) => ({
												...s,
												batch: {
													...s.batch,
													size: Number(v) as 1000000 | 250000,
												},
											}))
										}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="1000000">1,000,000</SelectItem>
											<SelectItem value="250000">250,000</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-2">
									<Label>CCPC per batch</Label>
									<Input
										type="number"
										min={0}
										step="0.01"
										value={state.batch.ccpcPerBatch}
										onChange={(e) =>
											setState((s) => ({
												...s,
												batch: {
													...s.batch,
													ccpcPerBatch: toNonNegativeNumber(e.target.value),
												},
											}))
										}
									/>
								</div>
								<div className="space-y-2">
									<Label>Serialization per unit</Label>
									<Input
										type="number"
										min={0}
										step="0.0001"
										value={state.serializationPerUnit}
										onChange={(e) =>
											setState((s) => ({
												...s,
												serializationPerUnit: toNonNegativeNumber(
													e.target.value,
												),
											}))
										}
									/>
								</div>
								{/* Parity fixed to corrected; selector removed */}
							</div>
						</CardContent>
					</Card>
				</div>

				<div className="space-y-4">
					<Card>
						<CardHeader>
							<CardTitle>Strength definitions</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							{state.strengths.map((s, idx) => (
								<div
									key={`${s.name}-${idx}`}
									className="grid grid-cols-12 gap-2 items-end"
								>
									<div className="col-span-4 space-y-1">
										<Label>Name</Label>
										<Input
											value={s.name}
											onChange={(e) =>
												setState((st) => ({
													...st,
													strengths: st.strengths.map((x, i) =>
														i === idx ? { ...x, name: e.target.value } : x,
													),
												}))
											}
										/>
									</div>
									<div className="col-span-2 space-y-1">
										<Label>Label mg</Label>
										<Input
											type="number"
											min={0}
											step="0.01"
											value={s.labelMg}
											onChange={(e) =>
												setState((st) => ({
													...st,
													strengths: st.strengths.map((x, i) =>
														i === idx
															? {
																	...x,
																	labelMg: toNonNegativeNumber(e.target.value),
																}
															: x,
													),
												}))
											}
										/>
									</div>
									<div className="col-span-2 space-y-1">
										<Label>Overage mg</Label>
										<Input
											type="number"
											min={0}
											step="0.01"
											value={s.overageMg}
											onChange={(e) =>
												setState((st) => ({
													...st,
													strengths: st.strengths.map((x, i) =>
														i === idx
															? {
																	...x,
																	overageMg: toNonNegativeNumber(
																		e.target.value,
																	),
																}
															: x,
													),
												}))
											}
										/>
									</div>
									<div className="col-span-2 space-y-1">
										<Label>Exp factor</Label>
										<Input
											type="number"
											min={0}
											step="0.0001"
											value={s.expFactor}
											onChange={(e) =>
												setState((st) => ({
													...st,
													strengths: st.strengths.map((x, i) =>
														i === idx
															? {
																	...x,
																	expFactor: toNonNegativeNumber(
																		e.target.value,
																	),
																}
															: x,
													),
												}))
											}
										/>
									</div>
									<div className="col-span-2 flex items-end gap-2">
										<Button
											variant="secondary"
											onClick={() =>
												setState((st) => ({
													...st,
													strengths: st.strengths.filter((_, i) => i !== idx),
												}))
											}
										>
											Delete
										</Button>
									</div>
								</div>
							))}
							<div className="flex justify-end">
								<Button
									onClick={() =>
										setState((st) => ({
											...st,
											strengths: [
												...st.strengths,
												{
													name: `S${st.strengths.length + 1}`,
													labelMg: 0,
													overageMg: 0,
													expFactor: 0,
												},
											],
										}))
									}
								>
									Add strength
								</Button>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>Packing materials per unit</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							{state.packagingMaterials.map((material) => (
								<div
									key={material.id}
									className="grid grid-cols-12 gap-2 items-end"
								>
									<div className="col-span-5 space-y-1">
										<Label>Name</Label>
										<Input
											value={material.name}
											onChange={(e) =>
												updatePackagingMaterial(
													material.id,
													"name",
													e.target.value,
												)
											}
										/>
									</div>
									<div className="col-span-4 space-y-1">
										<Label>Cost per unit</Label>
										<Input
											type="number"
											min={0}
											step="0.0001"
											value={material.costPerUnit}
											onChange={(e) =>
												updatePackagingMaterial(
													material.id,
													"costPerUnit",
													toNonNegativeNumber(e.target.value),
												)
											}
										/>
									</div>
									<div className="col-span-3 flex items-end gap-2">
										<Button
											variant="secondary"
											onClick={() => deletePackagingMaterial(material.id)}
											disabled={state.packagingMaterials.length <= 1}
										>
											Delete
										</Button>
									</div>
								</div>
							))}
							<div className="flex justify-end">
								<Button onClick={addPackagingMaterial}>
									Add packaging material
								</Button>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>

			{/* Derived unit-costs */}
			<div className="space-y-4">
				<Card>
					<CardHeader>
						<CardTitle>Derived unit-costs</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid grid-cols-2 gap-2 text-sm">
							<div>Total API price to hand (per kg)</div>
							<div className="text-right font-medium tabular-nums">
								{state.suppliers.map((supplier) => (
									<div key={supplier.id}>
										{formatCurrency(
											totalApiPricePerKgBySupplier[supplier.id] || 0,
											state.currency,
										)}{" "}
										({supplier.name})
									</div>
								))}
							</div>
							<div>CCPC per unit</div>
							<div className="text-right tabular-nums">
								{formatCurrency(ccpcPerUnit, state.currency)}
							</div>
							<div>Serialization per unit</div>
							<div className="text-right tabular-nums">
								{formatCurrency(state.serializationPerUnit, state.currency)}
							</div>
							{state.packagingMaterials.map((material) => (
								<React.Fragment key={material.id}>
									<div>PM per unit ({material.name})</div>
									<div className="text-right tabular-nums">
										{formatCurrency(material.costPerUnit, state.currency)}
									</div>
								</React.Fragment>
							))}
						</div>

						<div className="border-t pt-3 space-y-2">
							<div className="text-sm font-semibold">
								Per strength by supplier
							</div>
							{state.suppliers.map((supplier) => (
								<div key={supplier.id} className="space-y-2">
									<div className="text-sm font-medium text-muted-foreground">
										{supplier.name}
									</div>
									<div className="grid grid-cols-5 gap-2 text-xs font-medium text-muted-foreground">
										<div>Strength</div>
										<div className="text-right">API mg/unit</div>
										<div className="text-right">API $/unit</div>
										<div className="text-right">Exp $/unit</div>
										<div className="text-right">Exp factor</div>
									</div>
									{state.strengths.map((s) => {
										const strengthData = findStrengthBySupplier(
											supplier.id,
											s.name,
										);
										return (
											<div
												key={s.name}
												className="grid grid-cols-5 gap-2 text-sm"
											>
												<div className="truncate">{s.name}</div>
												<div className="text-right tabular-nums">
													{(strengthData?.apiPerUnitMg ?? 0).toFixed(4)}
												</div>
												<div className="text-right tabular-nums">
													{formatCurrency(
														strengthData?.apiPricePerUnit ?? 0,
														state.currency,
													)}
												</div>
												<div className="text-right tabular-nums">
													{formatCurrency(
														strengthData?.expPerUnit ?? 0,
														state.currency,
													)}
												</div>
												<div className="text-right tabular-nums">
													{s.expFactor.toFixed(4)}
												</div>
											</div>
										);
									})}
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			</div>

			{/* SKU Results and Summary */}
			<div className="space-y-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between">
						<CardTitle>SKU Results</CardTitle>
						<div className="flex items-center gap-2">
							<Button variant="outline" onClick={addSku}>
								Add SKU
							</Button>
							<Button variant="secondary" onClick={exportCsv}>
								Export CSV
							</Button>
						</div>
					</CardHeader>
					<CardContent className="space-y-3">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>
										<SortableHeader
											column="strength"
											sortConfig={state.sortConfig}
											onSort={handleColumnSort}
											className="justify-start"
										>
											Strength
										</SortableHeader>
									</TableHead>
									<TableHead>
										<SortableHeader
											column="packType"
											sortConfig={state.sortConfig}
											onSort={handleColumnSort}
											className="justify-start"
										>
											Pack
										</SortableHeader>
									</TableHead>
									<TableHead>
										<SortableHeader
											column="supplier"
											sortConfig={state.sortConfig}
											onSort={handleColumnSort}
											className="justify-start"
										>
											Supplier
										</SortableHeader>
									</TableHead>
									<TableHead className="text-right">
										<SortableHeader
											column="size"
											sortConfig={state.sortConfig}
											onSort={handleColumnSort}
											className="justify-end"
										>
											Size
										</SortableHeader>
									</TableHead>
									<TableHead className="text-right">
										<SortableHeader
											column="api"
											sortConfig={state.sortConfig}
											onSort={handleColumnSort}
											className="justify-end"
										>
											API / pack
										</SortableHeader>
									</TableHead>
									<TableHead className="text-right">
										<SortableHeader
											column="excipient"
											sortConfig={state.sortConfig}
											onSort={handleColumnSort}
											className="justify-end"
										>
											Excipient / pack
										</SortableHeader>
									</TableHead>
									<TableHead className="text-right">
										<SortableHeader
											column="pm"
											sortConfig={state.sortConfig}
											onSort={handleColumnSort}
											className="justify-end"
										>
											PM / pack
										</SortableHeader>
									</TableHead>
									<TableHead className="text-right">
										<SortableHeader
											column="ccpc"
											sortConfig={state.sortConfig}
											onSort={handleColumnSort}
											className="justify-end"
										>
											CCPC / pack
										</SortableHeader>
									</TableHead>
									<TableHead className="text-right">
										<SortableHeader
											column="serialization"
											sortConfig={state.sortConfig}
											onSort={handleColumnSort}
											className="justify-end"
										>
											Ser / pack
										</SortableHeader>
									</TableHead>
									<TableHead className="text-right">
										<SortableHeader
											column="total"
											sortConfig={state.sortConfig}
											onSort={handleColumnSort}
											className="justify-end"
										>
											Total
										</SortableHeader>
									</TableHead>
									<TableHead className="text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{sortedSkus.map(({ row: r, supplier }) => {
									const t = computeSkuTotals(r);
									const invalid = !t.valid;
									return (
										<TableRow key={`${r.id}-${supplier?.id ?? "unknown"}`}>
											<TableCell>
												<div className="space-y-1">
													<Select
														value={r.strength}
														onValueChange={(v: string) =>
															updateSku(r.id, "strength", v)
														}
													>
														<SelectTrigger
															className={invalid ? "aria-invalid" : undefined}
															aria-invalid={invalid}
														>
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															{state.strengths.map((s) => (
																<SelectItem key={s.name} value={s.name}>
																	{s.name}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</div>
											</TableCell>
											<TableCell>
												<Select
													value={r.packType}
													onValueChange={(v: PackType) =>
														updateSku(r.id, "packType", v)
													}
												>
													<SelectTrigger>
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														{state.packagingMaterials.map((material) => (
															<SelectItem
																key={material.id}
																value={material.name}
															>
																{material.name}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</TableCell>
											<TableCell>
												<Select
													value={r.supplierId}
													onValueChange={(v: string) =>
														updateSku(r.id, "supplierId", v)
													}
												>
													<SelectTrigger>
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														{state.suppliers.map((supplier) => (
															<SelectItem key={supplier.id} value={supplier.id}>
																{supplier.name}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</TableCell>
											<TableCell className="text-right">
												<Input
													type="number"
													min={1}
													step={1}
													value={r.packSize}
													onChange={(e) =>
														updateSku(
															r.id,
															"packSize",
															Math.max(
																1,
																Math.floor(Number(e.target.value) || 1),
															),
														)
													}
													className="w-full text-right tabular-nums"
												/>
											</TableCell>
											<TableCell className="text-right tabular-nums">
												{formatCurrency(t.apiPerPack, state.currency)}
											</TableCell>
											<TableCell className="text-right tabular-nums">
												{formatCurrency(t.excipientPerPack, state.currency)}
											</TableCell>
											<TableCell className="text-right tabular-nums">
												{formatCurrency(t.pmPerPack, state.currency)}
											</TableCell>
											<TableCell className="text-right tabular-nums">
												{formatCurrency(t.ccpcPerPack, state.currency)}
											</TableCell>
											<TableCell className="text-right tabular-nums">
												{formatCurrency(t.serializationPerPack, state.currency)}
											</TableCell>
											<TableCell className="text-right font-medium tabular-nums">
												{formatCurrency(t.totalPackCost, state.currency)}
											</TableCell>
											<TableCell className="text-right">
												<div className="flex gap-1">
													<Button
														variant="ghost"
														size="sm"
														onClick={() => duplicateSku(r.id)}
													>
														Duplicate
													</Button>
													<Button
														variant="destructive"
														size="sm"
														onClick={() => deleteSku(r.id)}
													>
														Delete
													</Button>
												</div>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>

						<div className="border-t pt-3 space-y-1 text-sm">
							<div className="font-semibold">
								Percent breakdowns (sum to 100%)
							</div>
							<div className="grid grid-cols-3 gap-2">
								<div>%API</div>
								<div className="text-center">%(Excipient + PM + Ser)</div>
								<div className="text-right">%CCPC</div>
							</div>
							{sortedSkus.map(({ row: r, supplier }) => {
								const t = computeSkuTotals(r);
								const pct = (n: number) => `${(n * 100).toFixed(2)}%`;
								return (
									<div
										key={r.id + "-" + (supplier?.id ?? "unknown") + "-pct"}
										className="grid grid-cols-3 gap-2 text-sm"
									>
										<div className="tabular-nums">{pct(t.percentApi)}</div>
										<div className="text-center tabular-nums">
											{pct(t.percentOther)}
										</div>
										<div className="text-right tabular-nums">
											{pct(t.percentCcpc)}
										</div>
									</div>
								);
							})}
						</div>
					</CardContent>
				</Card>

				{cheapestAndExpensive?.cheapest &&
					cheapestAndExpensive?.mostExpensive && (
						<Card>
							<CardHeader>
								<CardTitle>Global summary</CardTitle>
							</CardHeader>
							<CardContent className="text-sm grid grid-cols-2 gap-2">
								<div>Batch size</div>
								<div className="text-right">
									{state.batch.size.toLocaleString()}
								</div>
								<div>CCPC/unit</div>
								<div className="text-right tabular-nums">
									{formatCurrency(ccpcPerUnit, state.currency)}
								</div>
								<div>Cheapest SKU</div>
								<div className="text-right tabular-nums">
									{cheapestAndExpensive.cheapest.sku.row.strength}{" "}
									{cheapestAndExpensive.cheapest.sku.row.packType}{" "}
									{cheapestAndExpensive.cheapest.sku.row.packSize} —{" "}
									{formatCurrency(
										cheapestAndExpensive.cheapest.total,
										state.currency,
									)}{" "}
									(
									{cheapestAndExpensive.cheapest.sku.supplier?.name ??
										"Unknown Supplier"}
									)
								</div>
								<div>Most expensive SKU</div>
								<div className="text-right tabular-nums">
									{cheapestAndExpensive.mostExpensive.sku.row.strength}{" "}
									{cheapestAndExpensive.mostExpensive.sku.row.packType}{" "}
									{cheapestAndExpensive.mostExpensive.sku.row.packSize} —{" "}
									{formatCurrency(
										cheapestAndExpensive.mostExpensive.total,
										state.currency,
									)}{" "}
									(
									{cheapestAndExpensive.mostExpensive.sku.supplier?.name ??
										"Unknown Supplier"}
									)
								</div>
							</CardContent>
						</Card>
					)}
			</div>
		</div>
	);
}

export default App;
